import {
    Box, Divider, Typography, List, ListItemButton, ListItemText, Collapse, ListItemIcon, IconButton, useTheme, useMediaQuery, Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AssessmentIcon from '@mui/icons-material/Assessment';
import BiotechIcon from '@mui/icons-material/Biotech';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import CableIcon from '@mui/icons-material/Cable';
import ScienceIcon from '@mui/icons-material/Science';
import CategoryIcon from '@mui/icons-material/Category';
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import MenuBookIcon from "@mui/icons-material/MenuBook";

export default function AnalyzeResults({ options, refgen }) {
    const { pathname } = useLocation();
    const { id } = useParams();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const [isSidebarOpen, setSidebarOpen] = useState(false);



    const inResistance = useMemo(
        () => pathname.includes("/resistance"),
        [pathname]
    );

    const [resOpen, setResOpen] = useState(false);

    useEffect(() => {
        setResOpen(inResistance);
    }, [inResistance]);

    useEffect(() => {
        setSidebarOpen(!isSmallScreen);
    }, [isSmallScreen]);

    const showText = isSidebarOpen;

    const sidebarContent = (
        <List disablePadding>
            <Tooltip title={showText ? "" : "Expand"} placement="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isSidebarOpen ? 'space-between' : 'center', width: '100%' }}>
                    <Box onClick={() => setSidebarOpen(!isSidebarOpen)} sx={{ display: showText ? 'block' : 'none', cursor: 'pointer' }}>
                        <Typography variant="h5">
                            Analyze
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setSidebarOpen(!isSidebarOpen)} sx={{ color: '#fff' }}>
                        {isSidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>
                </Box>
            </Tooltip >

            {options?.multiqc === true &&
                <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.35)" }} />
            }

            {/* Quality */}
            {options?.multiqc === true &&
                <Box
                    sx={{
                        width: '100%',
                        fontWeight: 700,
                        whiteSpace: 'normal',   // 👈 IMPORTANTE
                        wordBreak: 'break-word'
                    }}
                >
                    <SideLink
                        to="quality"
                        icon={<AssessmentIcon />}
                        showText={showText}
                    >
                        {"Sequencing and assembly quality"}
                    </SideLink>
                </Box>
            }

            {(options?.resistanceGenes?.resfinder === true || options?.resistanceGenes?.rgi === true) &&
                <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.35)" }} />
            }


            {/* Resistance (con submenu) */}
            {(options?.resistanceGenes?.resfinder === true || options?.resistanceGenes?.rgi === true) &&
                <Tooltip title={showText ? "" : "Resistance genes"} placement="right">
                    <ListItemButton
                        onClick={() => setResOpen((v) => !v)}
                        sx={{
                            px: 0, py: 2, color: "#fff", opacity: 0.9,
                            "&:hover": { opacity: 1 },
                            width: '100%',
                            justifyContent: showText ? "initial" : "center",
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, mr: showText ? 3 : 0, justifyContent: "center", color: "inherit" }}>
                            <BiotechIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography
                                    sx={{
                                        opacity: showText ? 1 : 0,
                                        transition: "opacity 0.2s",
                                        display: showText ? "block" : "none"
                                    }}>
                                    Resistance genes
                                </Typography>
                            }
                            sx={{ display: showText ? "block" : "none" }}
                        />
                        {showText && <ExpandMoreIcon
                            sx={{
                                ml: 1,
                                transition: "transform 220ms ease",
                                transform: resOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                        />}
                    </ListItemButton>
                </Tooltip>
            }

            <Collapse in={resOpen} timeout={260} unmountOnExit>
                <List
                    disablePadding
                    sx={{
                        pl: 2,
                        borderLeft: "1px solid rgba(255,255,255,0.25)",
                        ml: 1
                    }}
                >
                    {options?.resistanceGenes?.resfinder === true &&
                        <SideSubLink
                            to="resistance/resfinder"
                            active={pathname.startsWith("resistance/resfinder")}
                            showText={showText}
                            title="ResFinder"
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Box sx={{
                                    width: '5px',
                                    height: '10px',
                                    borderTopRightRadius: 999,
                                    borderBottomRightRadius: 999,
                                    background: '#20BC71',
                                    mr: '7px'
                                }} />  <Typography sx={{ fontSize: '18x', textTransform: 'lowercase', opacity: showText ? 1 : 0, display: showText ? "block" : "none" }}>ResFinder</Typography>
                            </Box>
                        </SideSubLink>
                    }
                    {options?.resistanceGenes.rgi === true &&
                        <SideSubLink
                            to="resistance/rgi"
                            active={pathname.startsWith("resistance/rgi")}
                            showText={showText}
                            title="RGI"
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Box sx={{
                                    width: '5px',
                                    height: '10px',
                                    borderTopRightRadius: 999,
                                    borderBottomRightRadius: 999,
                                    background: '#20BC71',
                                    mr: '7px'
                                }} />  <Typography sx={{ fontSize: '18px', textTransform: 'lowercase', opacity: showText ? 1 : 0, display: showText ? "block" : "none" }}>RGI</Typography>
                            </Box>
                        </SideSubLink>
                    }
                </List>
            </Collapse>

            {options?.virulence === true &&
                <SideLinkOption
                    pathname={pathname}
                    title={"Virulence factors"}
                    path={"virulence"}
                    icon={<CoronavirusIcon />}
                    showText={showText}
                />
            }
            {options?.sccmec === true &&
                <SideLinkOption
                    pathname={pathname}
                    title={"Cromosomal cassettes"}
                    path={"sccmec"}
                    icon={<CableIcon />}
                    showText={showText}
                />
            }
            {options?.kaptive === true &&
                <SideLinkOption
                    pathname={pathname}
                    title={"Kaptive"}
                    path={"kaptive"}
                    icon={<ScienceIcon />}
                    showText={showText}
                />
            }
            {options?.mlst === true &&
                <SideLinkOption
                    pathname={pathname}
                    title={"mlst"}
                    path={"mlst"}
                    icon={<CategoryIcon />}
                    showText={showText}
                />
            }
            <SideLinkOption
                pathname={pathname}
                title={"Citations"}
                path={"citations"}
                icon={<MenuBookIcon />}
                showText={showText}
            />
            {/*  <SideLinkOption pathname={pathname} title={"Download"} path={"download"} icon={<DownloadIcon />} showText={showText} /> */}
        </List>
    );

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: isSidebarOpen ? "280px 1fr" : "70px 1fr",
                transition: "grid-template-columns 0.3s ease",
                mb: "125px",
                position: "relative",
                alignItems: "start",
                width: "100%",
            }}
        >
            {/* Sidebar */}
            <Box
                sx={{
                    width: isSidebarOpen ? 290 : 70,
                    bgcolor: "#000",
                    color: "#fff",
                    position: "sticky",
                    top: 65,
                    alignSelf: "start",
                    height: `calc(100vh - var(--nav-h, 90px))`,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pt: "24px",
                    px: isSidebarOpen ? "30px" : "12px",
                    transition: "width 0.3s ease, padding 0.3s ease",
                }}
            >
                {sidebarContent}
            </Box>

            {/* Content */}
            <Box
                sx={{
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <SideBarPageShell>
                    <Outlet context={{ refgen, options }} />
                </SideBarPageShell>
            </Box>
        </Box>
    );
}


const SideBarPageShell = ({ children }) => {
    return <Box sx={{ width: "100%", pt: 4, px: 6 }}>{children}</Box>;
};

/* ---------- Items del menú ---------- */


const SideLinkOption = ({ pathname, title, path, icon, showText }) => {
    return (
        <Box>
            <Divider
                sx={{
                    my: 3,
                    borderColor: "rgba(255,255,255,0.35)"
                }} />

            <SideLink
                to={path}
                active={pathname.startsWith(path)}
                icon={icon}
                showText={showText}
            >
                {title}
            </SideLink>
        </Box>
    )
}

function SideLink({ to, children, icon, showText = true }) {
    return (
        <Tooltip title={showText ? "" : children} placement="right">
            <ListItemButton
                component={NavLink}
                to={to}
                sx={{
                    px: 0,
                    justifyContent: showText ? "initial" : "center",
                    color: "rgba(255,255,255,0.7)",
                    overflow: "hidden", // para que no se rompa al animar width
                    transition: "all 220ms ease",
                    "&.active": {
                        color: "#fff",
                        transform: "translateX(6px)",
                        "& .MuiTypography-root": {
                            fontWeight: 600,
                        },
                    },
                }}
            >
                {icon && (
                    <ListItemIcon sx={{ minWidth: 0, mr: showText ? 3 : 0, justifyContent: "center", color: "inherit" }}>
                        {icon}
                    </ListItemIcon>
                )}
                <ListItemText
                    primary={
                        <Typography sx={{ opacity: showText ? 1 : 0, transition: "opacity 0.2s", whiteSpace: "normal", display: showText ? "block" : "none" }} variant="body1">
                            {children}
                        </Typography>
                    }
                    sx={{ display: showText ? "block" : "none" }}
                />
            </ListItemButton>
        </Tooltip>
    );
}

function SideSubLink({ to, children, showText = true, title }) {
    return (
        <Tooltip title={showText ? "" : title} placement="right">
            <ListItemButton
                component={NavLink}
                to={to}
                sx={{
                    px: showText ? 1.5 : 0,
                    py: 1.25,
                    justifyContent: showText ? "initial" : "center",
                    color: "rgba(255,255,255,0.8)",
                    transition: "all 200ms ease",
                    "&.active": {
                        color: "#fff",
                        transform: "translateX(4px)",
                        "& .MuiTypography-root": {
                            fontWeight: 600,
                        },
                    },
                    "&:hover": {
                        color: "#fff",
                        opacity: 1,
                    },
                }}
            >
                <Typography variant="body2">
                    {children}
                </Typography>
            </ListItemButton>
        </Tooltip>
    );
}
